/**
 * YEDEK KASASI — otomatik yedeğin anahtarı ve hedefi bu tarayıcıda nerede.
 *
 * NİÇİN IndexedDB, DEFTERİN SQLite'I DEĞİL:
 *  1. `CryptoKey` yalnızca yapısal kopyalamayla saklanabiliyor ve dışa
 *     çıkarılamaz bir anahtarın BAŞKA saklanma yolu yok (ham baytları yok).
 *     SQLite'a ancak ham bayt yazılabilir — o da "dışa çıkarılamaz" kararını
 *     bozmak olurdu.
 *  2. Defterin `settings` tablosu YEDEĞE GİRİYOR (`exportPlainJson`). Bulut
 *     yenileme anahtarını oraya yazmak, onu her yedek dosyasına gömmek
 *     demekti.
 *  3. Şema göçü gerektirmiyor (GÖÇ YOK).
 *
 * SİTE VERİSİ SİLİNİNCE BU KASA DA GİDER ve bu doğru davranış: anahtar
 * gidince geriye yalnızca parola kalır, dosyalar parolayla açılır. Kasayı
 * silinmeye karşı korumaya çalışmak (sunucuya yazmak gibi) kullanıcının
 * "sunucu hiçbir şey tutmaz" kararıyla çelişirdi.
 */

import { M } from "../dil";
import type { KilitPort } from "@parafon/contracts/local-api";
import type { HedefKaydi, KasaKaydi } from "./tipler";

export interface YedekKasasi {
  oku(): Promise<KasaKaydi | null>;
  yaz(kayit: KasaKaydi): Promise<void>;
  sil(): Promise<void>;
}

const DB_ADI = "parafon-yedek";
const DEPO = "kasa";
const ANAHTAR = "kurulum";

function istek<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error(M.yedek.kayit.idbIstek));
  });
}

function ac(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error(M.yedek.kayit.idbYokKurulamaz));
  }
  const r = indexedDB.open(DB_ADI, 1);
  r.onupgradeneeded = () => {
    if (!r.result.objectStoreNames.contains(DEPO)) r.result.createObjectStore(DEPO);
  };
  return istek(r);
}

async function islem<T>(
  kip: IDBTransactionMode,
  is: (depo: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await ac();
  try {
    const tx = db.transaction(DEPO, kip);
    const sonuc = await istek(is(tx.objectStore(DEPO)));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(M.yedek.kayit.idbIslem));
      tx.onabort = () => reject(tx.error ?? new Error(M.yedek.kayit.idbIptal));
    });
    return sonuc;
  } finally {
    db.close();
  }
}

export function createIndexedDbKasa(): YedekKasasi {
  return {
    async oku() {
      const v = (await islem("readonly", (d) => d.get(ANAHTAR))) as KasaKaydi | undefined;
      return v ?? null;
    },
    async yaz(kayit) {
      await islem("readwrite", (d) => d.put(kayit, ANAHTAR));
    },
    async sil() {
      await islem("readwrite", (d) => d.delete(ANAHTAR));
    },
  };
}

/** Testler ve IndexedDB'siz ortam için. */
export function createBellekKasa(ilk: KasaKaydi | null = null): YedekKasasi & {
  readonly anlik: () => KasaKaydi | null;
} {
  let kayit = ilk;
  return {
    oku: () => Promise.resolve(kayit),
    yaz: (k) => {
      kayit = k;
      return Promise.resolve();
    },
    sil: () => {
      kayit = null;
      return Promise.resolve();
    },
    anlik: () => kayit,
  };
}

// ---------------------------------------------------------------------------
// ÇOKLU PROFİL (Adım 2): kasa PROFİLİN ŞİFRELİ KABINDA
// ---------------------------------------------------------------------------
//
// Kayıt defterinin dışında yalnızca etiket, kimlik, tarih, KDF ve sarmallar
// durabilir (CLAUDE.md §8): yedek hedefi, son yedek zamanı ve bulut yenileme
// anahtarı da profilin kendi kabına girer (ayrılmış alan; dışa aktarılmaz,
// eşitlenmez). Kilitli defterin kasası v2'dir — `CryptoKey` taşımaz.
//
// TEK İSTİSNA yerel klasör tutamacı: `FileSystemDirectoryHandle` metne
// çevrilemez. O nesne ayrı bir IndexedDB deposunda, profille bağı kabın
// İÇİNDEKİ rastgele bir anahtarla kurulan bir kayıtta durur. Dışarıdan
// görülen: "bir klasör tutamacı var" ve klasörün adı — hangi profilin olduğu
// değil. Bilinçli sınır (DURUM).

const TUTAMAC_DB = "parafon-tutamaclar";
const TUTAMAC_DEPO = "t";

function tutamacIslem<T>(kip: IDBTransactionMode, is: (d: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error(M.yedek.kayit.idbYok));
  return new Promise<T>((ok, hata) => {
    const r = indexedDB.open(TUTAMAC_DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(TUTAMAC_DEPO)) r.result.createObjectStore(TUTAMAC_DEPO);
    };
    r.onerror = () => hata(r.error ?? new Error(M.yedek.kayit.idbAcilamadi));
    r.onsuccess = () => {
      const db = r.result;
      const tx = db.transaction(TUTAMAC_DEPO, kip);
      const q = is(tx.objectStore(TUTAMAC_DEPO));
      tx.oncomplete = () => {
        db.close();
        ok(q.result);
      };
      tx.onerror = () => {
        db.close();
        hata(tx.error ?? new Error(M.yedek.kayit.idbDustu));
      };
    };
  });
}

export interface TutamacDeposu {
  oku(anahtar: string): Promise<FileSystemDirectoryHandle | null>;
  yaz(anahtar: string, t: FileSystemDirectoryHandle): Promise<void>;
  sil(anahtar: string): Promise<void>;
}

export function createIndexedDbTutamacDeposu(): TutamacDeposu {
  return {
    async oku(a) {
      return ((await tutamacIslem("readonly", (d) => d.get(a))) as FileSystemDirectoryHandle | undefined) ?? null;
    },
    async yaz(a, t) {
      await tutamacIslem("readwrite", (d) => d.put(t, a));
    },
    async sil(a) {
      await tutamacIslem("readwrite", (d) => d.delete(a));
    },
  };
}

/** Kabın içindeki biçim: yerel klasörde tutamaç yerine onun rastgele anahtarı. */
type DepolananHedef = Exclude<HedefKaydi, { tur: "yerel-klasor" }> | { tur: "yerel-klasor"; tutamac: string; ad: string };

function rastgele(): string {
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export interface DefterKasasiSecenekleri {
  readonly tutamaclar: TutamacDeposu;
  /**
   * Adım 1'den kalan TEK IndexedDB kaydı: yalnızca EN ESKİ profile, ilk
   * okumada bir kez aktarılır ve oradan silinir (o kayıt o profile aitti).
   */
  readonly eski?: { readonly kasa: YedekKasasi; readonly buProfilEnEskiMi: () => Promise<boolean> };
}

export function createDefterKasasi(kilit: Pick<KilitPort, "kasaOku" | "kasaYaz">, o: DefterKasasiSecenekleri): YedekKasasi {
  const coz = async (metin: string): Promise<KasaKaydi | null> => {
    const k = JSON.parse(metin) as Omit<KasaKaydi, "hedef"> & { hedef: DepolananHedef };
    if (k.hedef.tur !== "yerel-klasor") return k as unknown as KasaKaydi;
    const klasor = await o.tutamaclar.oku(k.hedef.tutamac);
    // Tutamaç gitmişse (site verisi kısmen silinmiş) kayıt yok sayılır: yedek
    // "kapalı" görünür ve kullanıcı yeniden kurar — yanlış hedefe yazılmaz.
    if (klasor === null) return null;
    return { ...k, hedef: { tur: "yerel-klasor", klasor, ad: k.hedef.ad } } as unknown as KasaKaydi;
  };
  const eskiTutamac = async (): Promise<string | null> => {
    const m = await kilit.kasaOku();
    if (m === null) return null;
    const h = (JSON.parse(m) as { hedef: DepolananHedef }).hedef;
    return h.tur === "yerel-klasor" ? h.tutamac : null;
  };
  const kasa: YedekKasasi = {
    async oku() {
      const m = await kilit.kasaOku();
      if (m !== null) return coz(m);
      if (o.eski === undefined || !(await o.eski.buProfilEnEskiMi())) return null;
      const eski = await o.eski.kasa.oku();
      if (eski === null) return null;
      if (eski.surum !== 2) return null; // anahtarlı v1 kilitli defterde kullanılmaz (yedekleyici zaten çevirir)
      await kasa.yaz(eski);
      await o.eski.kasa.sil();
      return eski;
    },
    async yaz(k) {
      const onceki = await eskiTutamac();
      let hedef: DepolananHedef;
      if (k.hedef.tur === "yerel-klasor") {
        const anahtar = onceki ?? rastgele();
        await o.tutamaclar.yaz(anahtar, k.hedef.klasor);
        hedef = { tur: "yerel-klasor", tutamac: anahtar, ad: k.hedef.ad };
      } else {
        if (onceki !== null) await o.tutamaclar.sil(onceki);
        hedef = k.hedef;
      }
      await kilit.kasaYaz(JSON.stringify({ ...k, hedef }));
    },
    async sil() {
      const onceki = await eskiTutamac();
      await kilit.kasaYaz(null);
      if (onceki !== null) await o.tutamaclar.sil(onceki);
    },
  };
  return kasa;
}
